import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import SEO from '../seo/SEO'
import InfoPage from './InfoPage'

interface Question {
  q: string
  a: string
}

// One list feeds both the visible answers and the FAQPage structured data, so the two
// can't disagree (Google ignores FAQ markup that doesn't match the page).
const SECTIONS: { title: string; questions: Question[] }[] = [
  {
    title: 'Getting started',
    questions: [
      {
        q: 'What is MakeMyDay?',
        a: 'MakeMyDay is a well-being app that gives you one short uplifting message and one small, practical action every day, at the time you choose. Around that is a community where people share kind notes with each other.',
      },
      {
        q: 'Who is MakeMyDay for?',
        a: 'Anyone who would like a calmer, more positive start to the day: people building better habits, people going through a stressful period, or anyone who simply enjoys a kind word. It takes a minute or two a day and asks nothing more of you.',
      },
      {
        q: 'Is MakeMyDay free?',
        a: 'Yes. The daily message, the daily action, streaks and the community are free to use. The free version shows a small number of ads. A paid option, MakeMyDay Plus, is planned but not available to buy yet.',
      },
      {
        q: 'Where can I use MakeMyDay?',
        a: 'The MakeMyDay Telegram bot is available today. Our Android and iOS apps, which include the community and streaks, are being prepared for release, along with a web version.',
      },
      {
        q: 'Is MakeMyDay a substitute for professional help?',
        a: 'No. MakeMyDay offers everyday encouragement, not medical or psychological care. If you are struggling, please talk to someone you trust or a qualified professional, and if you are in danger, contact your local emergency services.',
      },
    ],
  },
  {
    title: 'Your daily message and action',
    questions: [
      {
        q: 'When will I receive my daily message?',
        a: 'At the hour you choose, in your own time zone. You can change the hour at any time, and if notifications are turned off, the message is simply waiting for you when you open the app.',
      },
      {
        q: 'Who writes the daily messages?',
        a: 'The messages and actions are written with the help of AI, in a voice our team designed to be warm, honest and never preachy.',
      },
      {
        q: 'What is the daily action?',
        a: 'A small, concrete thing to do that takes a few minutes at most, such as writing down something you are grateful for, taking a short walk or sending a kind message to a friend. When you have done it, you can mark it as done.',
      },
      {
        q: 'What is a streak, and what happens if I miss a day?',
        a: 'A streak counts how many days in a row you have completed your daily action. Missing a day resets the count, but nothing else is lost: recent messages stay available, and you can start a new streak the very next day.',
      },
    ],
  },
  {
    title: 'The community',
    questions: [
      {
        q: 'How many notes can I share?',
        a: 'Free accounts can share one community note a day, plus one more after watching an optional short video. The limit keeps the feed thoughtful rather than crowded.',
      },
      {
        q: 'Why wasn\'t my note published?',
        a: 'Every note is checked automatically as it is shared, and anything borderline is reviewed by our team. Notes need to be in English and can\'t include links, email addresses, phone numbers, or offensive or harmful content. If a note is turned down, the app tells you why so you can adjust it.',
      },
      {
        q: 'Can I edit or delete my notes?',
        a: 'Yes. You can edit or delete your own notes at any time. Edited notes are checked again before the new version appears.',
      },
      {
        q: 'How do I report a note or block someone?',
        a: 'Open the options on any note to report it or to block its author. Reported notes are reviewed by our team. Blocking takes effect immediately: neither of you sees the other\'s notes any more, any follows between you are removed, and the other person is not notified.',
      },
    ],
  },
  {
    title: 'Privacy and your account',
    questions: [
      {
        q: 'Do you sell my data?',
        a: 'No. We collect only what we need to run MakeMyDay and never sell personal data. Our Privacy Policy lists exactly what we store and why.',
      },
      {
        q: 'How are ads handled?',
        a: 'Ads are provided by Google. Where the law requires it, including in the EEA, the UK and Switzerland, we ask for your consent first, and you can change your choice at any time. The content of your notes is never shared with advertisers or used to target ads.',
      },
      {
        q: 'How do I delete my account?',
        a: 'You can delete your account from inside the app. It is hidden straight away and permanently deleted after a 30-day grace period. Signing back in during those 30 days restores it, in case you change your mind.',
      },
      {
        q: 'How can I contact you?',
        a: 'Email hello@makemyday.tips for questions and feedback, or info@makemyday.tips for privacy requests. You can also reach us on the social channels linked at the bottom of every page.',
      },
    ],
  },
]

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: SECTIONS.flatMap((s) =>
    s.questions.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  ),
}

const Faq: React.FC = () => (
  <>
    <SEO
      title="MakeMyDay FAQ | Answers to Common Questions"
      description="Answers to the most common questions about MakeMyDay: the daily message and action, streaks, sharing community notes, moderation, ads, privacy and deleting your account."
    />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
    <InfoPage
      subtitle="FAQ"
      title="Frequently Asked Questions"
      lead="Quick answers to the questions we hear most. If yours isn't here, write to us at hello@makemyday.tips."
    >
      {SECTIONS.map((section) => (
        <React.Fragment key={section.title}>
          <h2>{section.title}</h2>
          {section.questions.map(({ q, a }) => (
            <details key={q} className="faq-item">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </React.Fragment>
      ))}
      <p>
        Want the full picture? See <Link to="/how-it-works">How it works</Link>, read our{' '}
        <Link to="/privacy">Privacy Policy</Link>, or browse the longer FAQ in our{' '}
        <a href="https://docs.makemyday.tips/docs/faq/">documentation</a>, which also covers our
        roadmap and plans for creators.
      </p>
    </InfoPage>
  </>
)

export default Faq
